import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import * as LucideIcons from "lucide-react";

const HeroSection = () => {
    const [transactionCount, setTransactionCount] = useState(2847392);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);

        // Animate transaction counter
        const interval = setInterval(() => {
            setTransactionCount(prev => prev + Math.floor(Math.random() * 5) + 1);
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    const scrollToDemo = () => {
        const element = document.getElementById('demo-request');
        if (element) {
            element?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const scrollToTestimonials = () => {
        const element = document.getElementById('testimonials');
        if (element) {
            element?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <section className="relative min-h-screen bg-gradient-to-br from-primary/5 via-white to-accent/5 overflow-hidden">
            {/* Ghana Map Overlay */}
            <div className="absolute inset-0 opacity-10">
                <Image
                    src="https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=1200&h=800&fit=crop"
                    alt="Ghana Map"
                    className="w-full h-full object-cover"
                />
            </div>
            {/* Animated Revenue Flow Visualization */}
            <div className="absolute inset-0">
                {[...Array(6)]?.map((_, i) => (
                    <div
                        key={i}
                        className={`absolute w-2 h-2 bg-accent rounded-full animate-pulse ${
                            isVisible ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={{
                            left: `${20 + i * 15}%`,
                            top: `${30 + i * 10}%`,
                            animationDelay: `${i * 0.5}s`,
                            animationDuration: '2s'
                        }}
                    />
                ))}
            </div>
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Hero Content */}
                    <div className={`space-y-8 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
                        {/* Trust Badge */}
                        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-full border border-success/20">
                            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                            <span className="text-sm font-medium text-success">Ghana.gov Integrated Platform</span>
                        </div>

                        {/* Main Headline */}
                        <div className="space-y-4">
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                                Digitizing Revenue and
                                <span className="text-primary block">Service Delivery</span>
                                <span className="text-muted-foreground text-2xl sm:text-3xl lg:text-4xl block mt-2">
                  for Ghana's Local Assemblies
                </span>
                            </h1>

                            <p className="text-xl text-muted-foreground max-w-2xl">
                                Eliminate cash handling corruption with 100% digital transactions and full offline capabilities.
                                Transform your MMDA operations with government-compliant technology.
                            </p>
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button
                                variant="default"
                                size="lg"
                                onClick={scrollToDemo}
                                iconName="Calendar"
                                iconPosition="left"
                                className="text-lg px-8 py-4"
                            >
                                Request Demo
                            </Button>

                            <Button
                                variant="outline"
                                size="lg"
                                onClick={scrollToTestimonials}
                                iconName="Play"
                                iconPosition="left"
                                className="text-lg px-8 py-4"
                            >
                                View Success Stories
                            </Button>
                        </div>

                        {/* Live Transaction Counter */}
                        <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-border shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Digital Transactions</p>
                                    <p className="text-2xl font-bold text-primary">
                                        GH₵ {transactionCount?.toLocaleString()}
                                    </p>
                                </div>
                                <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
                            </div>
                        </div>
                    </div>

                    {/* Hero Visual */}
                    <div className={`relative ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
                        <div className="relative">
                            {/* Mobile Device Mockup */}
                            <div className="relative mx-auto w-80 h-96 bg-slate-900 rounded-3xl p-2 shadow-2xl">
                                <div className="w-full h-full bg-white rounded-2xl overflow-hidden">
                                    <div className="bg-primary p-4 text-white">
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-semibold">Civicrate Citizen Portal</h3>
                                            <Icon name="Smartphone" size={20} />
                                        </div>
                                    </div>

                                    <div className="p-4 space-y-4">
                                        <div className="grid grid-cols-2 gap-3">
                                            {[
                                                { name: 'Property Rates', amount: '₵450.00', icon: 'Home' },
                                                { name: 'Business Permit', amount: '₵1,200.00', icon: 'Building' },
                                                { name: 'Market Tolls', amount: '₵25.00', icon: 'ShoppingCart' },
                                                { name: 'Parking Fees', amount: '₵15.00', icon: 'Car' }
                                            ]?.map((service, index) => (
                                                <div key={index} className="bg-muted rounded-lg p-3 text-center">
                                                    <Icon name={service?.icon as keyof typeof LucideIcons} size={24} className="mx-auto mb-2 text-primary" />
                                                    <p className="text-xs font-medium">{service?.name}</p>
                                                    <p className="text-sm font-bold text-primary">{service?.amount}</p>
                                                </div>
                                            ))}
                                        </div>

                                        <Button variant="default" fullWidth className="mt-4">
                                            Pay with Mobile Money
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Floating Stats */}
                            <div className="absolute -top-4 -left-4 bg-white rounded-lg p-3 shadow-lg border border-border">
                                <div className="flex items-center space-x-2">
                                    <Icon name="TrendingUp" size={16} color="var(--color-success)" />
                                    <span className="text-sm font-medium">+340% Revenue</span>
                                </div>
                            </div>

                            <div className="absolute -bottom-4 -right-4 bg-white rounded-lg p-3 shadow-lg border border-border">
                                <div className="flex items-center space-x-2">
                                    <Icon name="Shield" size={16} color="var(--color-primary)" />
                                    <span className="text-sm font-medium">100% Secure</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trust Bar */}
                <div className="mt-16 pt-8 border-t border-border">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                        <div className="flex items-center justify-center space-x-3">
                            <Icon name="CheckCircle" size={24} color="var(--color-success)" />
                            <span className="font-medium">Ghana.gov Integration</span>
                        </div>

                        <div className="flex items-center justify-center space-x-3">
                            <Icon name="Shield" size={24} color="var(--color-primary)" />
                            <span className="font-medium">Data Protection Act Compliant</span>
                        </div>

                        <div className="flex items-center justify-center space-x-3">
                            <Icon name="Zap" size={24} color="var(--color-accent)" />
                            <span className="font-medium">Offline Capabilities</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroSection;